import { getDataReaderClient } from "@multiweb/clients/util";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue, IResultMessage, IRowData } from "@multiweb/types/contracts";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { IDBDataSourceParams, IEditableDataSource, IPermissionData, ISaveRowEventArgs } from "../../../Global/Base/DataSources/Interfaces";
import { AttrValue } from "../../../Global/Base/DataTypes/AttrValue";
import { AwaitableStandardEvent, Event } from "../../../Global/Base/Events";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { DeferredStateful, PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { getClassItemsRights } from "../../../Global/Base/Rights/utils";
import { ConditionOperator, StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { IValidationError } from "../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { EOWRecordPermissionException, EOWUserException, EOWValidateException } from "../../../OW.Remoting/Basic/OWExceptions";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { IExtendedObjectOperation } from "../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { ClassItemType } from "../../../OW.Remoting/Remoting/Rights";
import { DBDataSource } from "./DBDataSource";
import { IObjectDataSource } from "./IObjectDataSource";

const logger = LazyLoadingSectionLogger.create(LoggingSection.dataSource);

/**
 * Třída reprezentující databázový zdroj dat pro konzumenta dat
 */
export class EditableDBDataSource extends DBDataSource implements IEditableDataSource, IObjectDataSource, IDisposable {
    /**
     * Vrací informaci o zrušení změn
     */
    public readonly changesCancelled: Event<EditableDBDataSource, any> = new Event<EditableDBDataSource, any>();
    /**
    * Vrací nový objekt T
    */
    public readonly newCreated: AwaitableStandardEvent<EditableDBDataSource, Readonly<IRowData>> = new AwaitableStandardEvent<EditableDBDataSource, Readonly<IRowData>>();

    /** Vrací záznamy, u kterých právě začalo ukládání. Umožňuje před uložením provést dodatečné změny/kontroly */
    public readonly beforeSave: AwaitableStandardEvent<IEditableDataSource, Readonly<{ objects: ReadonlyArray<OWObject> }>> = new AwaitableStandardEvent<IEditableDataSource, Readonly<{ objects: ReadonlyArray<OWObject> }>>();

    /**
    * Vrací nový objekt T
    */
    public readonly saved: Event<EditableDBDataSource, Readonly<ISaveRowEventArgs>> = new Event<EditableDBDataSource, Readonly<ISaveRowEventArgs>>();

    /**
     * Vrací uložené záznamy po skončení ukládání
     */
    public readonly afterSaved: Event<EditableDBDataSource, Readonly<{ saved: ReadonlyArray<string> }>> = new Event<EditableDBDataSource, Readonly<{ saved: ReadonlyArray<string> }>>();

    /**
    * Vrací mazaný řádek
    * Parametr je key
    */
    public readonly deleted: Event<EditableDBDataSource, { key: string; maintainSelection: boolean }> = new Event<EditableDBDataSource, { key: string; maintainSelection: boolean }>();

    /**
    * Vrací novou instanci objektu
    */
    public readonly onObjectInstanceChanged: Event<EditableDBDataSource, OWObject> = new Event<EditableDBDataSource, OWObject>();

    /**
    * Vrací refreshnutý řádek
    */
    public readonly rowRefreshed: Event<EditableDBDataSource, Readonly<IRowData>> = new Event<EditableDBDataSource, Readonly<IRowData>>();

    /**
     * Událost vyvolaná při vyhodnocení práv
     */
    public readonly permissionsChanged: Event<EditableDBDataSource, Readonly<{ key: any; permissions: ReadonlyArray<IPermissionData> }>> = new Event<EditableDBDataSource, Readonly<{ key: any; permissions: ReadonlyArray<IPermissionData> }>>();

    protected _objects: Map<string, IObjectInfo> = new Map<string, IObjectInfo>();
    private _objectPromises: Map<string, Promise<IObjectInfo>> = new Map<string, Promise<IObjectInfo>>();
    private _objectsLoadingDeferred: DeferredStateful<void> = PromiseUtils.deferStateful<void>();// signalizuje probíhající dočítání objektů
    protected _editedObjects: Set<string> = new Set();
    protected _waitForUpdateMultiTask: Array<Promise<any>> = []; // provádí se víc operací zaráz, nelze to chainovat
    /** Čas v sekundach po který bude vrácena při getování objektu, s nastavením force load, vždy stejná instance */
    private static readonly _expirationTimeOnObject: number = 10;

    public permissionKey: string = "__permission_info";

    /**
     * Volitelná funkce umožňující řídit, zda se má objekt uvolnit
     */
    public objectDisposePredicate?: (objForDispose: OWObject) => boolean;

    public get editedObjectsCount(): number {
        return this._editedObjects.size;
    }

    public get isEditable(): boolean {
        return true;
    }

    /**
     * Pole instanceID objektů načtených ze serveru
     */
    public get loadedInstanceIDs(): string[] {
        return [...this._objects.values()]
            .map(obj => obj.actualObject.instanceID);
    }

    //#region Constructors
    constructor(params: IDBDataSourceParams) {
        super(params);
        this._setObjectsLoadingDeferred(true);
    }
    //#endregion Constructors

    /**
     * Vrátí hodnoty zadaných atributů
     */
    public async getValues(key: any, attributes?: string[]): Promise<IRowData | null> {
        await Promise.all(this._waitForUpdateMultiTask);
        this._waitForUpdateMultiTask = [];
        const objInfo = await this._getObject(key, null);
        return await this._getRowData(objInfo, attributes || this._attributes);
    }

    protected async _getRowData(objInfo: IObjectInfo, attributes: string[]): Promise<IRowData> {
        await this.waitForReload();
        const row: IRowData = {};
        await PromiseUtils.for(0, attributes.length, async (index) => {
            const attrName = attributes[index];
            row[attrName] = await objInfo.actualObject.getValue(attrName);
        });
        row[this.dataKey] = objInfo.key;
        row[this.permissionKey] = objInfo.permissions;
        return row;
    }

    public async setValues(key: any, values: IRowData): Promise<void> {
        const objInfo = this._getObjectInfo(key);
        objInfo.isChanged = true;
        const obj = objInfo.actualObject;
        this._editedObjects.add(objInfo.key);
        const promises = [...Object.keys(values)]
            .map(attrName => obj.setValue(attrName, values[attrName]).catch(PromiseUtils.catchAll));
        try {
            await Promise.all(promises);
        } catch (e) {
            this.error.trigger(this, e);
        }
    }

    /**
     * Načítá práva na atributy ze služby
     */
    private async _updateObjectRights(actObject: IOWObject, ct: CancellationToken): Promise<void> {
        const id = actObject.id.toString();
        const obj = this._getObjectInfo(id);

        if (!obj)
            return;

        obj.permissionsUpdateCTS?.cancel();

        const classItemsRightsList = this._attributes.map(attrName => ({
            name: this._firstAttrSegment(attrName),
            type: ClassItemType.Attribute,
        }));

        if (classItemsRightsList.length <= 0 || !actObject || !actObject.instanceID) {
            obj.permissions = [];
            return;
        }

        obj.permissionsUpdateCTS = new CancellationTokenSource(ct ? [ct] : undefined);
        const token = obj.permissionsUpdateCTS.token;
        // zavolá metodu na službu načte práva a nastaví na atributy
        obj.permissionsUpdate = getClassItemsRights(actObject, classItemsRightsList, this._getRequestContext(token)).then((perms) => { // TODO-rites: old rights - asi nechat
            if (token.cancellationRequested)
                return;

            obj.permissions = [...perms.values()].map(perm => ({
                attrName: perm.Name,
                enabled: perm.Enabled && actObject.canEdit,
                required: perm.Required,
                visible: perm.Visible,
            }));
            //logger.info("Reevaluated permissions for object id " + actObject.id, obj.permissions);
            this.permissionsChanged.trigger(this, { key: id, permissions: obj.permissions });
            obj.permissionsUpdate = undefined;
        }).catch(PromiseUtils.cancellationCatch);

        await obj.permissionsUpdate;
    }

    private _firstAttrSegment(attrName: string): string {
        const dotIndex = attrName.indexOf(".");
        if (dotIndex >= 0) {
            return attrName.slice(0, dotIndex);
        }
        return attrName;
    }

    /**
     * Vrátí aktuální oprávnění pro daný řádek
     * @param dataKey
     */
    public async getRowPermission(dataKey: string): Promise<IPermissionData[]> {
        const objInfo = this._getObjectInfo(dataKey);
        if (objInfo == null)
            return null;

        if (objInfo.permissionsUpdate)
            await objInfo.permissionsUpdate;

        return objInfo.permissions;
    }

    /**
     * Vytvoří nový řádek
     * @param extendedOperations Operace, které se mají provést ihned po založení nové instance aplikačního záznamu
     * @param objectName Název třídy, jejíž záznam se má založit, pokud není specifikovaný, tak se vezme třída nastavená na DataSource
     * @param attrNames Seznam atributů, které se mají z nového záznamu přečíst
     */
    public async newRow(extendedOperations?: IExtendedObjectOperation[], objectName?: string, attrNames?: string[], ct?: CancellationToken, instanceID?: string): Promise<INewRow> {
        const actualObject = instanceID ?
            await OWManager.getInstance(instanceID, undefined, undefined, this._getRequestContext(ct)) :
            await OWManager.newObject(objectName || this.className, extendedOperations, this._getRequestContext(ct));
        return await this._processNewObject(actualObject, attrNames, ct);
    }

    public async copyRow(objId: any, attrNames?: string[], extendedOperations?: IExtendedObjectOperation[]): Promise<INewRow> {
        const oldObject = await this.getObject(objId, extendedOperations);
        const actualObject = await oldObject.createCopy();
        return await this._processNewObject(actualObject, attrNames);
    }

    private async _processNewObject(newObject: OWObject, attrNames?: string[], _ct: CancellationToken = CancellationToken.none): Promise<INewRow> {
        const objInfo = await this._registerNewInstance(newObject, true);
        const row = await this._getRowData(objInfo, attrNames || this._attributes);
        await this.newCreated.trigger(this, row);

        // Záznam je také potřeba přidat do _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) nový záznam (vizuálně) ztrácel z LF (ale stále by "visel" v DF)
        const newRow: IRowData = { ...row };
        Object.keys(newRow).forEach((k) => newRow[k] == null && delete newRow[k]); // Z objektu se vyfiltrují pryč všechny null klíče
        delete newRow[this.permissionKey];
        newRow["ID"] = new AttrValue(newObject.id.toString());
        if (this._internalRows) {
            this._internalRows = [newRow, ...this._internalRows];
        } else {
            this._internalRows = [newRow];
        }

        return { actualObject: newObject, row };
    }

    protected async _registerNewInstance(obj: OWObject, isChanged: boolean, ct: CancellationToken = CancellationToken.none): Promise<IObjectInfo> {
        if (!obj) {
            return null;
        }
        const key = obj.id.toString();
        const objInfo: IObjectInfo = {
            key,
            actualObject: obj,
            isChanged: isChanged,
            permissions: [],
            expirationTime: EditableDBDataSource._getCurrentExpirationTime(),
        };
        this._objects.set(key, objInfo);
        if (isChanged) {
            this._editedObjects.add(key);
        }
        this._registerDisposedListener(objInfo);
        this._registerAttributesChangedListener(obj, ct);
        await this._updateObjectRights(obj, ct);
        return objInfo;
    }

    private _registerAttributesChangedListener(obj: OWObject, ct: CancellationToken = CancellationToken.none): void {
        if (obj) {
            this._events.addWithKey(obj.instanceID, obj.attributesChanged, (_, attrName) => this._attributesChangedListener(obj, attrName, ct));
        }
    }

    protected _registerDisposedListener(objInfo: IObjectInfo): void {
        const obj = objInfo?.actualObject;
        if (obj) {
            this._events.addWithKey(obj.instanceID, obj.disposed, () => {
                const actualObjInfo = this._getObjectInfo(obj.instanceID);
                if (actualObjInfo?.actualObject.instanceID === obj.instanceID) {
                    this._objects.delete(actualObjInfo.key);
                }
                this._events.removeByKey(obj.instanceID);
                objInfo.permissionsUpdateCTS?.cancel();
            });
        }
    }

    /**
     * Vrací čas v sekundach, po který bude vrácena při getování objektu, s nastavením force load, vždy stejná instance
     */
    private static _getCurrentExpirationTime(): Date {
        const expDate = new Date;
        expDate.setSeconds(expDate.getSeconds() + EditableDBDataSource._expirationTimeOnObject); // Teď + 10 sekund

        return expDate;
    }

    /**
     * Vrátí objekt s daným id
     * @param id Id požadovaného objektu
     * @param forceLoad Zda se má vynutit znovunačtení objektu ze služby (přeskočí cache)
     */

    public async getObject(id: string, extendedOperations: IExtendedObjectOperation[] | null, forceLoad: boolean = false, _ct?: CancellationToken): Promise<OWObject> {
        return (await this._getObject(id, extendedOperations, forceLoad))?.actualObject;
    }

    protected async _getObject(id: string, extendedOperations: IExtendedObjectOperation[] | null, forceLoad: boolean = false, ct?: CancellationToken): Promise<IObjectInfo> {
        let oldObjInfo = this._getObjectInfo(id);
        id = oldObjInfo?.key || id;

        if (oldObjInfo && oldObjInfo.actualObject && oldObjInfo.actualObject.isDisposed) //pokud byl objekt odstraněn, zahodí stav a načte novou instanci
            oldObjInfo = null;

        if (ObjectUtils.isIdTemporary(id) && oldObjInfo == null)
            return undefined;

        // TODO: zjistit proč je isChanged zvlášť zde na wrapperu a zvlášť na objektu
        if (oldObjInfo != null && (oldObjInfo.isChanged || !oldObjInfo.actualObject.isValid || (!forceLoad && oldObjInfo.expirationTime > new Date))) {
            return oldObjInfo;
        }

        if (this._objectPromises.has(id)) {
            try {
                return await this._objectPromises.get(id);
            } catch (err) {
                PromiseUtils.cancellationCatch(err);
            }
        }

        const oldInstanceID = oldObjInfo?.actualObject.instanceID || "";
        const getObjectInfo = async () => {
            const obj = await OWManager.getObject(id, this.className, extendedOperations, undefined, this._readVersioned, this._getRequestContext(ct));
            if (obj.id.toString() === "-666") {
                throw new EOWRecordPermissionException();
            }

            if (obj.instanceID !== oldInstanceID) {
                this.onObjectInstanceChanged.trigger(this, obj);
            }

            return await this._registerNewInstance(obj, false);
        };
        const getPromise = getObjectInfo();
        this._objectPromises.set(id, getPromise);
        this._setObjectsLoadingDeferred();
        try {
            return await getPromise;
        } finally {
            this._objectPromises.delete(id);
            if (this._objectPromises.size === 0)
                this._setObjectsLoadingDeferred(true);
        }
    }

    /**
     * Nastavuje odložený promise _objectsLoadingDeferred signalizující probíhající dočítání objektů.
     * @param resolve Pokud je true, nastavuje promise na resolved. Pokud false, vytváří nový promise.
     */
    private _setObjectsLoadingDeferred(resolve: boolean = false): void {
        if (!resolve) {
            if (this._objectsLoadingDeferred.isResolved)
                this._objectsLoadingDeferred = PromiseUtils.deferStateful<void>();
        } else {
            if (!this._objectsLoadingDeferred.isResolved)
                this._objectsLoadingDeferred.resolve();
        }
    }

    /** Čekání na probíhající dočítání objektů. */
    private async _waitForObjectsLoading(): Promise<void> {
        return this._objectsLoadingDeferred.promise;
    }

    public async deleteObject(key: string, ct?: CancellationToken, maintainSelection: boolean = false): Promise<IResultMessage[] | undefined> {
        const objInfo = this._getObjectInfo(key);
        if (!objInfo) {
            return undefined;
        }
        const obj = objInfo.actualObject;
        const result = await obj.delete(ct);

        this._internalRows = this._internalRows.filter(r => r.ID.toString() != key); // Záznam je potřeba odstranit z _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) záznam stále zobrazoval v LF

        this._cleanObject(objInfo.key);
        this.deleted.trigger(this, { key, maintainSelection });
        return result;
    }

    /**
     * Počká na doběhnutí změn všech objektů, na kterých zrovna probíhá UpdateInstance
     */
    protected _waitForInstanceUpdates(ct?: CancellationToken): Promise<any> {
        // změněný objekt se do kolekce editedObjects dostane až ve chvíli, až se vrátí odpověď z UpdateInstance a zpracuje se pomocí OWObject.LoadData()
        // zde je potřeba čekat, až ty dotazy doběhnou
        const pendingUpdates = [...this._objects.values()]
            .filter(obj => obj.actualObject?.isInstanceUpdateInProgress)
            .map(obj => obj.actualObject.waitForUpdateInstance(ct));
        return Promise.all(pendingUpdates);
    }

    /**
     * Umožňuje počkat na probíhájící načítání a update dat
     */
    public override waitForReload(ct?: CancellationToken): Promise<void> {
        return super.waitForReload(ct).then(() => this._waitForInstanceUpdates(ct).then(() => this._waitForObjectsLoading()));
    }

    //TODO: Po uložení nebo zrušení změn dořešit mazání nových sloupců nebo id
    public async saveObjects(saveOnlyKeys?: string[], forceApplyUpdates?: boolean, ct: CancellationToken = CancellationToken.none): Promise<SaveResultData[]> {
        const resultData: SaveResultData[] = [];
        const savedObjects: Array<string> = [];

        await Promise.all(this._waitForUpdateMultiTask);
        this._waitForUpdateMultiTask = [];
        ct.throwIfCancellationRequested();

        await this._waitForInstanceUpdates(ct);

        try {
            // Ten slice při kopírování je nutný, aby se udělala deep copy. this._editedObjects se v průběhu ukládání modifikuje
            const objectsToSave = saveOnlyKeys ?
                [...this._editedObjects].filter(obj => saveOnlyKeys.indexOf(obj) !== -1) :
                [...this._editedObjects];

            await this.beforeSave.trigger(this, { objects: objectsToSave.map((key) => this._getObjectInfo(key).actualObject) });

            await PromiseUtils.for(0, objectsToSave.length, async (index) => {
                ct.throwIfCancellationRequested();

                const id = objectsToSave[index];

                const objInfo = this._getObjectInfo(id);
                if (!objInfo) { // TODO [JB]: K této situaci by nemělo dojít a tudíž by tady neměla být potřeba ani tato podmínka
                    logger.warn(`Object '${id}' not found!`);
                    savedObjects.push(id);
                    return;
                }

                const obj = objInfo.actualObject;

                if (!forceApplyUpdates) {
                    if (!obj.isValid) {
                        return;
                    }
                }

                try {
                    const wasNew = obj.isNew;
                    resultData.push({
                        data: obj,
                        messages: await obj.applyUpdates(),
                    });

                    if (wasNew) {
                        await this._persistObject(obj.instanceID);
                    } else {
                        this._getObjectInfo(id).isChanged = false;
                        savedObjects.push(id);
                        this.saved.trigger(this, { key: id, newKey: obj.id.toString() });
                    }
                } catch (e) {
                    if (e instanceof EOWValidateException)
                        resultData.push({ data: obj, errors: e.validationErrors });
                    else if (e instanceof EOWUserException)
                        resultData.push({ data: obj, messages: e.resultMessages });
                    else
                        throw e;
                }
            });
        } finally {
            savedObjects.forEach(iid => this._editedObjects.delete(iid));
            this.afterSaved.trigger(this, { saved: savedObjects });
        }

        return resultData;
    }

    protected _translateInstanceId(instanceId: string): string {
        instanceId = instanceId?.toString() || "";
        if (ObjectUtils.isIdTemporary(instanceId) && !this._objects.get(instanceId)) {
            return [...this._objects.values()].find(objInfo => objInfo.actualObject.instanceID === instanceId)?.actualObject.id.toString() ||
                instanceId;
        }
        return instanceId;
    }

    /**
     * Fires the attributesChanged event and updates the list of objects in edit mode.
     * @param obj Changed object.
     * @param attributeNames List of changed attributes.
     */
    protected async _attributesChangedListener(obj: IOWObject, attributeNames: string[], ct: CancellationToken): Promise<void> {
        await this._persistObject(obj.instanceID);
        const objInfo = this._getObjectInfo(obj.id);
        const id: string = obj.id.toString();

        // pokud před odpovědí UpdateInstance došlo ke zrušení změn, tak je potřeba změny zahodit, protože instance objektu s nimi už byla taky zahozena
        if (objInfo?.actualObject == null || objInfo.actualObject.instanceID !== obj.instanceID)
            return;

        const lowerDsAttributes = this._attributes.map(dsAttr => dsAttr.toLocaleLowerCase());
        const filteredAttributeNames = attributeNames.filter(attr => {
            const lowerAttr = attr.toLowerCase();
            return lowerDsAttributes.some(dsAttr => dsAttr.startsWith(lowerAttr));
        });
        const promises = filteredAttributeNames.map(attrName => obj.getValue(attrName, ct));
        promises.map(promise => this._waitForUpdateMultiTask.push(promise.catch(PromiseUtils.catchAll)));
        const attributesMap = new Map<string, any>((await Promise.all(promises)).map((value, index) => [filteredAttributeNames[index], value]));

        // AttributeChanged se vyvolává i po refreshi objektu, kdy objekt není změněný
        if (obj.isChanged) {
            this._editedObjects.add(objInfo.key);
            objInfo.isChanged = obj.isChanged;
        } else { // Je třeba prověřit, zda k uložení nedošlo jinde (např. díky enableInEdit)
            this._editedObjects.delete(objInfo.key);
        }

        this.attributesChanged.trigger(this, {
            key: objInfo.key,
            instanceId: obj.instanceID,
            isChanged: obj.isChanged,
            values: attributesMap,
            context: obj,
        });

        this._waitForUpdateMultiTask.push(this._updateObjectRights(obj, ct).catch(PromiseUtils.catchAll));

        if (obj.isChanged && this._internalRows && this._internalRows.length) { // Atribut je potřeba změnit také v _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) změny (vizuálně) ztrácely v LF
            attributesMap.forEach((attrName, value) => this._changeRowVal(id, obj.instanceID, attrName, value));
        }
    }

    /**
     * Changes attribute value in this._internalRows.
     * @param id Object id.
     * @param instanceID Object instanceID - sometimes the record in _internalRows is stored under it (at least in case of a new record - in MSLOV you can't add records via the "+" button, but this is an inherited function from the ancestor where it is possible).
     * @param attributeName Attribute name.
     * @param value New value.
     */
    protected _changeRowVal(id: string, instanceID: string, attributeName: string, value: IAttrValue): void {
        const row = this._internalRows.find(r => {
            return r.ID === id || r.ID === instanceID;
        });
        if (row) {
            row[attributeName] = value;
        }
    }

    protected async _persistObject(instanceId: string, ct: CancellationToken = CancellationToken.none): Promise<void> {
        const objInfo = this._objects.get(instanceId);
        const obj = objInfo?.actualObject;
        if (!objInfo || !obj || obj.isNew) {
            return;
        }
        this._objects.delete(instanceId);
        const key = obj.id.toString();
        if (this._objects.has(key)) {
            return;
        }
        this._objects.set(key, {
            ...objInfo,
            key,
            isChanged: obj.isChanged,
            permissions: [],
            expirationTime: EditableDBDataSource._getCurrentExpirationTime(),
        });
        this._editedObjects.delete(instanceId);
        if (obj.isChanged) {
            this._editedObjects.add(key);
        }
        await this._updateObjectRights(obj, ct);
        this.saved.trigger(this, { key: obj.instanceID, newKey: key });
    }

    /**
     * Zrušení provedených změn
     * @param key - ID záznamu, na kterém rušíme změny
     * @param forceRefreshCancelledRecord - Znovu načte záznam, na kterém byly zrušeny změny
     * @param maintainSelection - Pokud došlo k zahození nového záznamu, zkusí označit záznam na pozici místo něj nebo nad ním.
     */
    public async cancelChanges(key: string = "", forceRefreshCancelledRecord: boolean = false, maintainSelection = false): Promise<void> {
        const ids: string[] = [];

        if (!StringUtils.isNullOrEmpty(key)) {
            const objInfo = this._getObjectInfo(key);
            if (objInfo?.actualObject?.isNew) {
                this.deleted.trigger(this, { key, maintainSelection });
                this._internalRows = this._internalRows.filter(r => r.ID.toString() != objInfo?.actualObject?.id); // Záznam je potřeba odstranit z _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) záznam stále zobrazoval v LF
                forceRefreshCancelledRecord = false;
            } else if (objInfo?.actualObject && !objInfo.isChanged && !objInfo.actualObject.isValid) {
                this._triggerObjectAttributesChanged(objInfo.actualObject); // nezměněné nevalidní záznamy není potřeba znovu načítat - stačí zpropagovat jejich původní hodnoty
                return;
            } else {
                ids.push(key);
            }

            this._cleanObject(key);
        } else {
            forceRefreshCancelledRecord = false;

            // zrušíme všechny probíhající UpdateInstance, jelikož jsou zbytečné a výsledek bychom stejně zahodili
            this._objects.forEach(record => {
                if (record.actualObject != null)
                    record.actualObject.cancelUpdatesInProgress();
            });

            this._objects.forEach((objInfo, key) => {
                if (!objInfo.isChanged) {
                    if (objInfo.actualObject && !objInfo.actualObject.isValid) // nezměněné nevalidní záznamy není potřeba znovu načítat - stačí zpropagovat jejich původní hodnoty
                        this._triggerObjectAttributesChanged(objInfo.actualObject);
                    return;
                }
                if (objInfo.actualObject?.isNew) {
                    this.deleted.trigger(this, { key, maintainSelection });
                    this._internalRows = this._internalRows.filter(r => r.ID.toString() != objInfo?.actualObject?.id); // Záznam je potřeba odstranit z _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) záznam stále zobrazoval v LF
                    return;
                }
                ids.push(key);
            });

            this.changesCancelled.trigger(this, null);

            this._editedObjects.clear();

            this._cleanObjects();
        }

        if (forceRefreshCancelledRecord) {
            const obj = await this.getObject(key, null, true);
            this._triggerObjectAttributesChanged(obj);
        } else if (ids.length > 0) {
            const data = await this._reloadRowsById(ids);
            const datamap = new Map<string, any>();

            for (const row of data) {
                const datagroup = new Map<string, any[]>();
                const okeys = ArrayUtils.getUniqueArray([...Object.keys(row), ...this._attributes]);
                let datakey: any = null;
                // přiřazení prázdných attributů, aby se nahradili všechny attributy
                for (const attr of this._attributes) {
                    datamap.set(attr, "");
                }

                for (const key of okeys) {
                    // grupování hodnot, zjistí se, zda je to hodnota přes vazbu, pokud ano uloží se jako groupa
                    //#region Grupovaní
                    const groupKeys = key.split(".");
                    if (groupKeys.length >= 2) {
                        let a: any[] = [];
                        if (datagroup.has(groupKeys[0]))
                            a = datagroup.get(groupKeys[0]);
                        a.push(row[key]);

                        datagroup.set(groupKeys[0], a); // přiřadí novou grupu
                    }
                    //#endregion Grupovaní

                    if (key === this.dataKey) {
                        datakey = row[key];
                        continue;
                    }
                    datamap.set(key, row[key]);
                }

                // přiřazení group
                for (const grp of datagroup) {
                    datamap.set(grp[0], grp[1]);
                }

                if (!datakey)
                    logger.warn("Datakey is not valid");

                this.attributesChanged.trigger(this, {
                    key: datakey,
                    isChanged: false,
                    values: datamap,
                });

                if (this._internalRows) {// Záznam je potřeba upravit na původní hodnoty atributů i v _internalRows, jinak by se při změně rozložení formuláře (kompakt X normální zobrazení) v LF stále zobrazovali upravené hodnoty
                    this._replaceInternalRow(row);
                }
            }
        }
    }

    /**
     * Nahradí záznam v this._internalRows (využívá se při rušení změn hodnot atributů)
     * @param newRow Nový záznam, kterým nahrazujeme ten původní.
     */
    protected _replaceInternalRow(newRow: IRowData): void {
        const changedRowIndex = this._internalRows.findIndex(r => {
            return r.ID?.toString() == newRow.ID.toString();
        });
        const temp = [...this._internalRows];
        temp[changedRowIndex] = newRow;
        this._internalRows = temp;
    }

    /**
     * Vrátí sloupce podle seznamu ID
     */
    private _reloadRowsById(ids: string[] = [], cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly IRowData[]> {
        if (ids.length > 0) {
            let idList = "ID = -1";
            for (const key of ids) {
                idList += ` OR ID='${key}'`; //`
            }
            const fullCond = StringUtils.combineConditions(ConditionOperator.and, idList, this._condition);

            return getDataReaderClient().getDataRowsWithParams(this.className, this._attributes, fullCond, { params: this._conditionParams, readVersioned: this._readVersioned }, this._fullLengthAttributes, this._getRequestContext(cancellationToken));
        } else {
            return Promise.resolve([]);
        }
    }

    /**
     * Uvolní instance needitovaných objektů
     */
    private _cleanObjects(keepEdited: boolean = true, objectDisposePredicate?: (objForDispose: OWObject) => boolean): void {
        this._objects.forEach(objInfo => {
            if (!objInfo.isChanged && keepEdited)
                return;

            if (objectDisposePredicate != null && !objectDisposePredicate(objInfo.actualObject))
                return;

            this._cleanObject(objInfo.key);
        });
    }

    private _cleanObject(key: string): void {
        this._editedObjects.delete(key);
        const objToClean = this._objects.get(key);
        if (!objToClean) {
            return;
        }

        this._objects.delete(key);
        objToClean.actualObject.dispose();
    }

    /**
     * Oznámení o změně všech načítaných atributů nově načteného objektu
     * @param obj
     */
    protected async _triggerObjectAttributesChanged(obj: OWObject, originalID?: string, ct?: CancellationToken): Promise<void> {
        const values = new Map<string, any>();

        const loadAttributeFunc = async (attr: string) => {
            try {
                values.set(attr, await obj.getValue(attr));
            } catch (ex) {
                // TODO: chtělo by to možná pro tuhle výjimku udělat speciální typ
                if (!(ex.message && /Class [\d\w_]+ doesn't contain attribute/ig.test(ex.message) && attr.includes("|")))
                    throw ex;
            }
        };

        await Promise.all(this._attributes.map(attribute => loadAttributeFunc(attribute)));

        //Pokud mi jiný uživatel smaže záznam, na který jsem klikl, tak obj má ID = -666 a potřebuju vědět původní ID, abych označil řádek.
        const id = obj.id === "-666" && originalID != null ? originalID : obj.id;

        //při změně výběru řádku v listFramu docházelo k rušení předchozího dotazu, tak že dotaz nedoběhl a ve values byly nedočtené hodnoty
        //a řádek v listframu se následně vykreslil jako prázdný. Problém byl v tom, že se getValues ukončilo, ale potom se ještě provedlo překreslení řádku
        ct?.throwIfCancellationRequested();

        this.attributesChanged.trigger(this, {
            key: id,
            instanceId: obj.instanceID,
            isChanged: false,
            values: values,
            context: obj,
        });
    }

    protected _getObjectInfo(key: string): IObjectInfo {
        return this._objects.get(this._translateInstanceId(key)) || this._objects.get(key?.toString());
    }

    public override dispose(): void {
        this._cleanObjects(false, this.objectDisposePredicate);

        this.changesCancelled.removeAll();
        this.newCreated.removeAll();
        this.beforeSave.removeAll();
        this.saved.removeAll();
        this.afterSaved.removeAll();
        this.deleted.removeAll();
        this.onObjectInstanceChanged.removeAll();
        this.rowRefreshed.removeAll();
        this.permissionsChanged.removeAll();

        super.dispose();
    }
}

export interface IObjectInfo {
    key: string;
    actualObject: OWObject;
    isChanged: boolean;
    permissionsUpdate?: Promise<void>;
    permissionsUpdateCTS?: CancellationTokenSource;
    permissions?: IPermissionData[];
    expirationTime: Date;
}

export interface INewRow {
    row: IRowData;
    actualObject: OWObject;
}

export interface IActualObjectChangeEventArgs {
    /**
     * Unikátní klíč objektu, v tomto případě ID
     */
    key: any;
    /**
     * Unikátní klíč objektu před vygenerováním ID
     */
    instanceId?: string;
    /**
     * Zda změna objektu reprezentuje změnu atributu na novou hodnotu (true) nebo na původní nezměněnou (false)
     */
    isChanged: boolean;
    /**
     * Kolekce názvů změněných atributů a jejich nových hodnot
     */
    values: Map<string, any>;
    /**
     * Příznak, že objekt byl právě dešifrován
     */
    isDecrypted?: boolean;
    /**
     * Objekt, u kterého dochází ke změně atributů
     */
    context?: IOWObject;
}

export interface SaveResultData {
    data: OWObject | null;
    /** Příznak že zpracování bylo odloženo na pozadí a doběhně někdy v budoucnu */
    isInLongTransaction?: boolean;
    messages?: IResultMessage[];
    errors?: IValidationError[];
}
